import React, { FC, memo, useEffect, useState } from 'react';
import useFlag from '../../hooks/useFlag';
import { GlobalState } from '../../global/types';
import { pick } from '../../util/iteratees';
import { getActions, withGlobal } from '../../global';
import Modal from '../ui/Modal';
import IconSvg from '../ui/IconSvg';
import Button from '../ui/Button';
import { useTranslation } from 'react-i18next';
import useLastCallback from '../../hooks/useLastCallback';
import useHistoryBack from '../../hooks/useHistoryBack';
import {
  ERoutes,
  LeftColumnContent,
  MiddleColumnContent,
  SettingsScreens,
} from '../../types';
import ConfirmDialog from '../ui/ConfirmDialog';
import { formatDateToString } from '../../util/dateFormat';

type StateProps = Pick<GlobalState, 'aiPurchases' | 'aiPrompts' | 'wallets'>;

const DAY_ALERT = 1;

const AiPromptNatifications: FC<StateProps> = ({
  aiPrompts,
  aiPurchases,
  wallets,
}) => {
  const { setAiPurchasesAlert } = getActions();
  const { t } = useTranslation();
  const { toggleScreens } = useHistoryBack();
  const {
    text_sub_day_alert,
    text_day_left = 0,
    img_sub_day_alert,
    double_sub_day_alert,
    text_sub_active,
    text_expiration_date,
    img_sub_active,
    img_expiration_date,
    img_day_left = 0,
    double_expiration_date,
    double_sub_active,
    double_day_left = 0,
    current_unix = 0,
  } = aiPurchases || {};

  const { amount } = wallets.find(({ type }) => type === 'main') || {};
  const textPrice = aiPrompts && aiPrompts[0].unlim;
  const imgPrice = aiPrompts && aiPrompts[1].unlim;
  const doublePrice = aiPrompts && aiPrompts[2].unlim;

  const [isOpenNotFundsModal, openNotFundsModal, closeNotFundsModal] =
    useFlag();
  const [isOpenExpiredEndModal, openExpiredEndModal, closeExpiredEndModal] =
    useFlag();

  const [expired, setExpired] = useState<number>();

  const onOpenEndExpired = useLastCallback((date) => {
    setExpired(date);
    openExpiredEndModal();
  });

  const handleTopUpWallet = useLastCallback(() => {
    toggleScreens({
      pathname: ERoutes.elloPay,
      leftScreen: LeftColumnContent.Settings,
      middleScreen: MiddleColumnContent.Settings,
      settingScreen: SettingsScreens.Wallet,
    });
    closeModal();
  });

  const closeModal = useLastCallback(() => {
    closeNotFundsModal();
  });

  useEffect(() => {
    let alert = {};
    if (
      amount &&
      (text_expiration_date || img_expiration_date || double_expiration_date)
    ) {
      if (
        text_expiration_date &&
        text_expiration_date - current_unix > 0 &&
        text_day_left < DAY_ALERT &&
        text_sub_day_alert !== text_day_left
      ) {
        if (!text_sub_active) {
          onOpenEndExpired(text_expiration_date);
        } else if (textPrice && amount < textPrice) {
          openNotFundsModal();
        }
        alert = { text_sub_day_alert: text_day_left };
      } else if (
        img_expiration_date &&
        img_expiration_date - current_unix > 0 &&
        img_day_left < DAY_ALERT &&
        img_day_left !== img_sub_day_alert
      ) {
        if (!img_sub_active) {
          onOpenEndExpired(img_expiration_date);
        } else if (imgPrice && amount < imgPrice) {
          openNotFundsModal();
        }
        alert = { img_sub_day_alert: img_day_left };
      } else if (
        double_expiration_date &&
        double_expiration_date - current_unix > 0 &&
        double_day_left < DAY_ALERT &&
        double_day_left !== double_sub_day_alert
      ) {
        if (!double_sub_active) {
          onOpenEndExpired(double_expiration_date);
        } else if (doublePrice && amount < doublePrice) {
          openNotFundsModal();
        }
        alert = { double_sub_day_alert: double_day_left };
      }

      if (alert) {
        setAiPurchasesAlert(alert);
      }
    }
  }, [
    amount,
    text_expiration_date,
    img_expiration_date,
    double_expiration_date,
  ]);

  return (
    <>
      <Modal
        isOpen={isOpenNotFundsModal}
        className='confirm payment-confirm'
        onClose={closeModal}
        centered
        hasCloseButton
      >
        <div className='modal-content'>
          <div className='logo-wrap'>
            <IconSvg name='ai-bot' w='74' h='74' />
          </div>
          <p>
            You do not have enough funds in your account to renew your
            subscription. Please top up your wallet to continue using the
            service.
          </p>

          <div className='dialog-buttons'>
            <Button onClick={handleTopUpWallet}>{t('Wallet.TopUp')}</Button>
            <Button outline onClick={closeModal}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={isOpenExpiredEndModal}
        onClose={closeExpiredEndModal}
        title='Renew Suscription'
        text={t('PaymentAi.YourSubscriptionExpires', {
          date: formatDateToString(expired! * 1000),
        })}
        confirmLabel={String(t('Confirm'))}
        confirmHandler={closeExpiredEndModal}
        withoutCancel
      />
    </>
  );
};

export default memo(
  withGlobal(
    (global): StateProps =>
      pick(global, ['aiPrompts', 'aiPurchases', 'wallets'])
  )(AiPromptNatifications)
);
